<template>
  <div data-component-group="payment-form" data-component="add-new">
    <slot name="beforeForm" />

    <form @submit.prevent="onSubmit">
      <magic-container>
        <div :class="getFirstRowClass()">
          <v-text-field
            v-model="id"
            prepend-inner-icon="fingerprint"
            label="ID"
            required
            type="text"
            :readonly="isDisabled"
          />
        </div>
        <div v-if="!isOpened" xs6>
          <v-text-field
            v-model="linkedInvoice"
            prepend-inner-icon="receipt"
            label="Linked invoice"
            required
            type="text"
            :readonly="isDisabled"
          />
        </div>
      </magic-container>

      <magic-container>
        <v-autocomplete
          v-model="business"
          :items="businesses"
          :loading="isLoadingBusinesses"
          prepend-inner-icon="contacts"
          label="Business"
          item-title="name"
          item-value="id"
          :return-object="true"
          required
          :readonly="isDisabled"
          v-model:search="businessSearch"
        />
        <v-text-field
          v-model="contractId"
          prepend-inner-icon="receipt"
          label="Price contract ID"
          required
          type="text"
          :readonly="isDisabled"
        />
      </magic-container>

      <v-text-field
        v-model="legalBusinessName"
        prepend-inner-icon="business"
        label="Legal business name"
        required
        type="text"
        :readonly="isDisabled"
      />

      <magic-container>
        <v-text-field
          v-model="country"
          label="Cost center"
          prepend-inner-icon="public"
          type="text"
          :readonly="isDisabled"
        />
        <v-text-field
          v-model="createdAt"
          label="Created at"
          prepend-inner-icon="date_range"
          type="text"
          :readonly="isDisabled"
        />
      </magic-container>

      <v-select
        v-model="status"
        prepend-inner-icon="change_history"
        label="Status"
        required
        item-title="name"
        item-value="id"
        :readonly="isDisabled"
      />
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import { isStr, isObj, isNum, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    setup () {
      return {
        v$: useVuelidate()
      }
    },
    validations() {
      return {
        id: {
          required,
        },
        linkedInvoice: !this.isOpened ? { required } : {},
        status: {
          required,
        },
        business: {
          required,
        },
        contractId: {
          required,
        },
        createdAt: {
          required,
        },
      };
    },
    props: {
      payment: {
        type: Object,
        default: undefined,
      },
      cancelTriggerLabel: {
        type: String,
        default: 'Cancel',
      },
      confirmTriggerLabel: {
        type: String,
        default: 'Add payment',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        default: undefined,
      },
    },
    data() {
      return {
        id: '',
        linkedInvoice: '',
        business: null,
        businessSearch: '',
        businesses: [],
        contractId: '',
        legalBusinessName: '',
        country: '',
        createdAt: '',
        status: null,
        isSubmitted: false,
        isLoadingBusinesses: false,
        isRefreshingBusiness: false,
      };
    },
    computed: {
      isOpened() {
        const { name = '' } = this.status || {};
        return name.toLowerCase() === 'open';
      },
      hasItem() {
        return isObj(this.payment);
      },
    },
    watch: {
      payment(nextPayment) {
        this.clear();
        this.onReceivePayment(nextPayment);
      },
      business() {
        this.isRefreshingBusiness = true;
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusiness) {
            this.isRefreshingBusiness = false;
            return false;
          }

          debouncer(() => this.getBusinesses(nextSearch));
        }
      },
    },
    created() {
      this.onReceivePayment(this.payment);
    },
    methods: {
      onReceivePayment(nextPayment) {
        if (isObj(nextPayment) && isNum(nextPayment.id)) {
          this.extractPaymentData(nextPayment);
        }
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItems', query)
          .then(({ items }) => {
            this.businesses = items;
            return items;
          })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      cancel() {
        this.clear();
        this.onCancel();
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.addPayment();
      },
      clear() {
        this.v$.$reset();
        this.id = '';
        this.linkedInvoice = '';
        this.business = null;
        this.contractId = '';
        this.legalBusinessName = '';
        this.createdAt = '';
        this.status = null;
        this.isSubmitted = false;
        this.isRefreshingBusiness = false;
      },
      addPayment() {
        const { id, business } = this;

        const payment = {
          id,
          business: business.id,
        };

        this.isSubmitted = true;
        return this.onSubmit(payment).then((res) => {
          if (!this.hasItem) {
            return this.clear();
          }

          return res;
        });
      },
      canSubmitRequest() {
        const { id, isSubmitted } = this;

        return id !== '' && !isSubmitted;
      },
      extractPaymentData(payment) {
        const { id, status, business, contract, invoice, created_at } = payment;
        const firstAccount = contract.account;

        this.id = id;
        this.contractId = contract.id;
        this.legalBusinessName = firstAccount.legal_business_name;
        this.country = contract.country.name;
        this.createdAt = created_at;
        this.status = status;

        if (isObj(invoice)) {
          this.linkedInvoice = invoice.invoice_number;
        }

        this.business = business;
        this.businesses = [this.business];
        this.isRefreshingBusiness = true;
      },
      getFirstRowClass() {
        return {
          xs12: this.isOpened,
          xs6: !this.isOpened,
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='payment-form'][data-component='add-new'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin-top: 0;
      margin-bottom: 0;

      &.trigger {
        .v-icon {
          margin: 0 6px 0 0;
        }
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
